// @system — Default navigation pages
// Every product inherits these automatically.
// Products can override any entry by declaring the same `path` in @custom/navigation.js.
// System defaults cover: home, notifications, the permission-gated admin area, profile, billing.
// These are the "boring but essential" pages every SaaS needs.

/**
 * Default section definitions.
 * Products can override by declaring sections with the same `key`.
 * @type {Array<{key: string, label: string|null, order: number}>}
 */
export const systemSections = [
  { key: 'main',    label: null,        order: 1 },
  { key: 'admin',   label: 'Admin',     order: 10 },
  { key: 'account', label: 'Account',   order: 90 },
]

/**
 * Default navigation pages.
 * Products get these for free. Override by path in @custom/navigation.js.
 * @type {import('./navigation-merge').PageEntry[]}
 */
export const systemPages = [
  // ── Main ──────────────────────────────────────────────
  {
    path: '/app',
    label: 'Home',
    icon: 'Home',
    sidebar: true,
    topbar: false,
    section: 'main',
    order: 1,
    requiresAuth: true,
  },

  // ── Main ──────────────────────────────────────────────
  {
    path: '/app/notifications',
    label: 'Notifications',
    icon: 'Bell',
    sidebar: true,
    topbar: false,
    section: 'main',
    order: 2,
    requiresAuth: true,
    // Badge dot shows the live unread count (fed by AppLayout via `badges.inbox`).
    badgeKey: 'inbox',
    badgeColor: 'default',
  },

  // ── Admin (permission-gated) ──────────────────────────
  // Every entry names the permission the server checks on its API route
  // (lib/@system/roles.js). filterPagesByAccess() hides entries the current
  // role does not hold, so the sidebar mirrors the matrix edited on
  // /app/admin/staff instead of a hard-coded "admin" role.
  {
    path: '/app/admin',
    label: 'Admin',
    icon: 'ShieldCheck',
    sidebar: true,
    section: 'admin',
    order: 10,
    requiresAuth: true,
    requiredPermission: 'platform.admin',
  },
  {
    path: '/app/admin/cancellations',
    label: 'Cancellations',
    icon: 'Percent',
    sidebar: true,
    section: 'admin',
    order: 11,
    requiresAuth: true,
    requiredPermission: 'billing.read',
    // Feature module (brand.json `modules.billing`): hidden by filterPagesByModules() when off.
    module: 'billing',
  },
  {
    path: '/app/admin/blog',
    label: 'Blog',
    icon: 'FileText',
    sidebar: true,
    section: 'admin',
    order: 12,
    requiresAuth: true,
    requiredPermission: 'content.write',
    module: 'blog',
  },
  {
    path: '/app/integrations',
    label: 'Integrations',
    icon: 'Plug',
    sidebar: true,
    section: 'admin',
    order: 13,
    requiresAuth: true,
    requiredPermission: 'platform.admin',
  },
  {
    path: '/app/admin/staff',
    label: 'Staff & roles',
    icon: 'Users',
    sidebar: true,
    section: 'admin',
    order: 14,
    requiresAuth: true,
    requiredPermission: 'staff.manage',
  },

  // ── Account ───────────────────────────────────────────,
  {
    path: '/app/profile',
    label: 'Profile',
    icon: 'User',
    sidebar: true,
    topbar: true,
    section: 'account',
    order: 91,
    requiresAuth: true,
  },
  {
    path: '/app/billing',
    label: 'Billing',
    icon: 'CreditCard',
    sidebar: true,
    topbar: false,
    section: 'account',
    order: 92,
    requiresAuth: true,
    // Feature module (brand.json `modules.billing`): hidden by
    // filterPagesByModules() when billing is off.
    module: 'billing',
  },
]
